import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { apiAxios as axios } from "../../utils/apiClient.js";
import Lottie from "react-lottie";
import emptyCartAnimation from "../../Assets/animated-json/empty_cart.json";
import "./MyOrderScreen.css";
import { useAuth } from "../../utils/AuthContext";
import { url } from "../../Constant/TextConstant";
import Header from "../Header";
import { useNetworkStatus } from "../../utils/userNetworkStatus";
import { useUserLocation } from "../../utils/useUserLocation";
import { encryptData } from "../../utils/encryption";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet";

const MyOrderScreen = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${url.baseAPIUrl}/api/orderdetails/GetOrders?CustomerMobile=${currentUser}`,
          { headers: { Accept: "application/json" } }
        );

        if (response.status === 200) {
          const data = response.data;
          const grouped = {};

          data.forEach((order) => {
            const orderNumber = order.OrderNumber || "N/A";
            if (!grouped[orderNumber]) {
              grouped[orderNumber] = {
                OrderNumber: orderNumber,
                Products: [],
                Date: order.Date || new Date().toISOString(),
                Name: order.Name || "N/A",
                FullAddress: order.FullAddress || "N/A",
                DeliveryStatus: order.DeliveryStatus || "Processing",
                TotalAmount: parseFloat(order.Total) || 0,
                ShopkeeperMobile: order.ShopkeeperMobile || "",
                StoreName: order.StoreName || "",
              };
            }

            // Only add product if there's product information
            if (order.ProductName || order.Price) {
              grouped[orderNumber].Products.push({
                name: order.ProductName || "Unnamed Product",
                image: order.Image || "",
                id: order.ID || 0,
                deliveryPin: order.DeliveryPin || "N/A",
                price: parseFloat(order.Price) || 0,
                quantity: parseInt(order.Quantity) || 1,
              });
            }
          });

          const ordersArray = Object.values(grouped);
          setOrders(ordersArray);
          setFilteredOrders(ordersArray);
        } else {
          throw new Error("Failed to load orders");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser]);

  useEffect(() => {
    filterOrders();
  }, [searchQuery, selectedDate, activeCategory, orders]);

  // In your filterOrders function, replace it with this:
  const filterOrders = () => {
    const query = searchQuery?.trim().toLowerCase();
    const normalizedCategory = activeCategory?.toLowerCase();

    // Fix for date handling
    const targetDate = selectedDate
      ? new Date(selectedDate).toISOString().split("T")[0]
      : null;

    const filtered = orders.filter((order) => {
      const {
        OrderNumber = "",
        ShopkeeperMobile = "",
        StoreName = "",
        Date: orderDate = "",
        Products = [],
        DeliveryStatus = "",
      } = order;

      // Search filter
      const matchesQuery =
        !query ||
        OrderNumber.toLowerCase().includes(query) ||
        (ShopkeeperMobile && ShopkeeperMobile.toLowerCase().includes(query)) ||
        (StoreName && StoreName.toLowerCase().includes(query)) ||
        Products.some(
          (product) =>
            product?.name && product.name.toLowerCase().includes(query)
        );

      // Date filter - handle invalid dates
      let matchesDate = true;
      if (targetDate) {
        try {
          const orderDateObj = orderDate ? new Date(orderDate) : null;
          if (orderDateObj && !isNaN(orderDateObj.getTime())) {
            const orderDateStr = orderDateObj.toISOString().split("T")[0];
            matchesDate = orderDateStr === targetDate;
          } else {
            matchesDate = false;
          }
        } catch (e) {
          matchesDate = false;
        }
      }

      // Category filter
      const normalizedStatus = normalizeStatus(DeliveryStatus).toLowerCase();
      const matchesCategory =
        normalizedCategory === "all" || normalizedStatus === normalizedCategory;

      return matchesQuery && matchesDate && matchesCategory;
    });

    setFilteredOrders(filtered);
  };

  function normalizeStatus(status) {
    if (!status) return "pending";

    const statusLower = status.toLowerCase();

    if (statusLower.includes("processing") || statusLower.includes("pending")) {
      return "Pending";
    }
    if (statusLower.includes("accept") || statusLower.includes("approved")) {
      return "Accepted";
    }
    if (statusLower.includes("pickup") || statusLower.includes("pick-up")) {
      return "Pickup";
    }
    if (statusLower.includes("shipped") || statusLower.includes("dispatch")) {
      return "Shipped";
    }
    if (statusLower.includes("delivered") || statusLower.includes("complete")) {
      return "Delivered";
    }
    if (
      statusLower.includes("cancelled") ||
      statusLower.includes("cancel") ||
      statusLower.includes("reject")
    ) {
      return "Cancelled";
    }
    return status;
  }

  const statusCategories = [
    { id: "all", title: "All Orders" },
    { id: "Pending", title: "Pending" },
    { id: "Accepted", title: "Accepted" },
    { id: "Pickup", title: "Pick Up" },
    { id: "Shipped", title: "Shipped" },
    { id: "Delivered", title: "Delivered" },
    { id: "Cancelled", title: "Cancelled" },
  ];

  const handleOrderClick = (orderNumber) => {
    navigate(`/myorderdetails/${encryptData(orderNumber)}`);
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const options = { year: "numeric", month: "short", day: "numeric" };
      return date.toLocaleDateString(undefined, options);
    } catch (e) {
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString) => {
    try {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return date.toLocaleDateString(undefined, options);
    } catch (e) {
      return "Invalid Date";
    }
  };

  const downloadInvoice = async (order) => {
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();

      // Add logo
      try {
        const logoUrl = `${window.location.origin}/assets/images/newLogo.png`;
        const logoResponse = await fetch(logoUrl);
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
          doc.addImage(logoDataUrl, "PNG", 80, 10, 50, 30);
        }
      } catch (logoError) {
      }

      // Invoice header
      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.setFont("helvetica", "bold");
      doc.text("ORDER INVOICE", 105, 50, { align: "center" });

      // Invoice details
      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");
      doc.text(`Invoice #: ${order.OrderNumber}`, 20, 70);
      doc.text(`Date: ${formatDate(order.Date)}`, 20, 80);
      doc.text(`Status: ${order.DeliveryStatus}`, 20, 90);

      // Customer information
      doc.setFont("helvetica", "bold");
      doc.text("Bill To:", 20, 110);
      doc.setFont("helvetica", "normal");
      doc.text(`Name: ${order.Name}`, 30, 120);
      doc.text(`Mobile: ${currentUser}`, 30, 130);
      doc.text(`Address: ${order.FullAddress}`, 30, 140);

      // Line items header
      doc.setFillColor(230, 230, 230);
      doc.rect(20, 150, 170, 10, "F");
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Item", 20, 157);
      doc.text("Price", 120, 157);
      doc.text("Qty", 150, 157);
      doc.text("Total", 170, 157);

      // Line items
      doc.setFont("helvetica", "normal");
      let yPosition = 175;
      let subtotal = 0;

      order.Products.forEach((product, index) => {
        const price = product.price || product.OurPrice || 0;
        const quantity = product.quantity || product.OurQuantity || 1;
        const total = price * quantity || 0;
        subtotal += total;

        doc.text(`${index + 1}. ${product.name}`, 20, yPosition);
        doc.text(`₹${price.toFixed(2)}`, 120, yPosition);
        doc.text(`${quantity}`, 150, yPosition);
        doc.text(`₹${total.toFixed(2)}`, 170, yPosition);
        yPosition += 10;
      });

      // Total section
      doc.setFont("helvetica", "bold");
      doc.text("Subtotal:", 150, yPosition + 30);
      doc.text(`₹${subtotal.toFixed(2)}`, 180, yPosition + 30);
      doc.text("Grand Total:", 150, yPosition + 40);
      doc.text(`₹${subtotal.toFixed(2)}`, 180, yPosition + 40);

      // Footer
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("Thank you for your business!", 105, 280, { align: "center" });
      doc.text(
        "Terms & Conditions: Goods once sold will not be taken back or exchanged.",
        105,
        285,
        { align: "center" }
      );

      // Save the PDF
      doc.save(`invoice_${order.OrderNumber}.pdf`);
    } catch (error) {
      alert("Failed to generate invoice. Please try again.");
    }
  };

  const OrderCard = ({ order }) => {
    const statusColor =
      {
        pending: "#FFA500", // Orange
        accept: "#4169E1", // Blue
        pickup: "#800080", // Purple
        shipped: "#1E90FF", // Dodger Blue
        delivery: "#32CD32", // Lime Green
        cancelled: "#FF0000", // Red
      }[normalizeStatus(order.DeliveryStatus)] || "#6c757d"; // Default gray

    return (
      <motion.div
        className="myOrder-card-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div
          className="myOrder-status-container"
          style={{ backgroundColor: `${statusColor}15` }}
        >
          <div className="myOrder-status-row">
            <div className="myOrder-status-info">
              <div className="myOrder-status-text">
                Status: {order.DeliveryStatus}
              </div>
              <div className="myOrder-status-date">
                {formatDateTime(order.Date)}
              </div>
            </div>
            <div className="myOrder-total-amount">
              Total: ₹{order.TotalAmount}
            </div>
          </div>
        </div>

        <div className="myOrder-info-container">
          <div className="myOrder-info">
            <div className="myOrder-number">Order #{order.OrderNumber}</div>
            {order.StoreName && (
              <div className="myOrder-store-name">Store: {order.StoreName}</div>
            )}
            <div className="myOrder-actions">
              <button
                className="myOrder-view-details-btn"
                onClick={() => handleOrderClick(order.OrderNumber)}
              >
                View Order Details
              </button>
              <button
                className="myOrder-download-invoice-btn"
                onClick={() => downloadInvoice(order)}
              >
                Download Invoice
              </button>
            </div>
          </div>

          {/* Product Details Section - Only show if there are products */}
          {order.Products.length > 0 && (
            <div className="myOrder-products-section">
              <h4 className="myOrder-products-title">Products:</h4>
              {order.Products.map((product, index) => (
                <div key={index} className="myOrder-product-item">
                  <div className="myOrder-product-image-container">
                    <img
                      src={`${url.productImageStartingPath}/${product.image}`}
                      alt={product.name}
                      className="myOrder-product-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://via.placeholder.com/100?text=Product";
                      }}
                    />
                  </div>
                  <div className="myOrder-product-details">
                    <div className="myOrder-product-name">{product.name}</div>

                    <div className="myOrder-product-delivery-pin">
                      Delivery PIN: {product.deliveryPin || "N/A"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Address Section */}
          <div className="myOrder-address-section">
            <h4 className="myOrder-address-title">Delivery Address:</h4>
            <p className="myOrder-full-address">{order.FullAddress}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  const EmptyCart = ({ onShopNow, hasFilters }) => {
    const defaultOptions = {
      loop: true,
      autoplay: true,
      animationData: emptyCartAnimation,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid slice",
      },
    };

    return (
      <>
        <div className="myOrder-empty-container">
          <div className="myOrder-empty-animation">
            <Lottie options={defaultOptions} height={250} width={250} />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="myOrder-empty-title">
              {hasFilters ? "No Matching Orders" : "No Orders Found"}
            </h2>
            <p className="myOrder-empty-text">
              {hasFilters
                ? "No orders match your current filters. Try adjusting your search criteria."
                : "You haven't placed any orders yet. Start shopping to see your orders here!"}
            </p>
            <button className="myOrder-empty-button" onClick={onShopNow}>
              {hasFilters ? "🔄 Clear Filters" : "🛍️ Shop Now"}
            </button>
          </motion.div>
        </div>
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="myOrder-loading">
        <div className="myOrder-spinner"></div>
        <span className="myOrder-loading-text">Loading your orders...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="myOrder-error-container">
        <div className="myOrder-error-alert">
          <span className="myOrder-error-icon">⚠️</span>
          <div>
            <h3 className="myOrder-error-title">Error loading orders</h3>
            <p className="myOrder-error-message">{error}</p>
          </div>
        </div>
        <button
          className="myOrder-error-button"
          onClick={() => window.location.reload()}
        >
          🔄 Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`My Orders (${filteredOrders.length}) | MyStore`}</title>
        <meta
          name="description"
          content={`View and manage your ${filteredOrders.length} orders. Track order status, download invoices, and view order history.`}
        />
        <meta
          name="keywords"
          content="my orders, order history, track orders, order status, invoices, MyStore"
        />
        <meta
          property="og:title"
          content={`My Orders (${filteredOrders.length}) | MyStore`}
        />
        <meta
          property="og:description"
          content={`View and manage your ${filteredOrders.length} orders on MyStore`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:image"
          content={`${window.location.origin}/orders-og-image.jpg`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`My Orders (${filteredOrders.length}) | MyStore`}
        />
        <meta
          name="twitter:description"
          content={`View your ${filteredOrders.length} orders on MyStore`}
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/orders-twitter-image.jpg`}
        />
        <link rel="canonical" href={window.location.href} />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "My Orders",
            "description": "List of your recent orders",
            "url": "${window.location.href}",
            "numberOfItems": ${filteredOrders.length},
            "itemListOrder": "http://schema.org/ItemListOrderDescending",
            "itemListElement": [
              ${filteredOrders
                .slice(0, 5)
                .map(
                  (order, index) => `{
                "@type": "ListItem",
                "position": ${index + 1},
                "item": {
                  "@type": "Order",
                  "orderNumber": "${order.OrderNumber}",
                  "orderDate": "${order.Date}",
                  "orderStatus": "${order.DeliveryStatus}",
                  "price": "${order.TotalAmount}",
                  "priceCurrency": "INR",
                  "seller": {
                    "@type": "Organization",
                    "name": "${order.StoreName || "MyStore"}"
                  }
                }
              }`
                )
                .join(",")}
            ]
          }`}
        </script>
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div className="myOrder-screen-container">
        <div className="myOrder-screen-header">
          <h1 className="myOrder-title">Your Orders</h1>
          <div className="myOrder-search-container">
            <input
              type="text"
              placeholder="Search by order #, store name, mobile or product"
              className="myOrder-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button className="myOrder-search-button">Search</button>
          </div>
        </div>

        <div className="myOrder-categories">
          {statusCategories.map((category) => (
            <button
              key={category.id}
              className={`myOrder-category-tab ${
                activeCategory === category.id ? "myOrder-active" : ""
              }`}
              onClick={() => setActiveCategory(category.id)}
            >
              {category.title}
            </button>
          ))}
        </div>

        <div className="myOrder-date-filter-container">
          <p className="myOrder-date-filter-text">Filter orders by date:</p>
          <input
            type="date"
            className="myOrder-date-filter-input"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>

        <div className="myOrder-count-info">
          Showing {filteredOrders.length}{" "}
          {filteredOrders.length === 1 ? "order" : "orders"}
          {(searchQuery || selectedDate || activeCategory !== "all") &&
            " matching your criteria"}
        </div>

        <AnimatePresence>
          {filteredOrders.length === 0 ? (
            // In your main return statement, replace the EmptyCart usage with:
            <EmptyCart
              onShopNow={() => {
                if (searchQuery || selectedDate || activeCategory !== "all") {
                  // Clear filters if they're active
                  setSearchQuery("");
                  setSelectedDate("");
                  setActiveCategory("all");
                } else {
                  navigate("/");
                }
              }}
              hasFilters={
                !!searchQuery || !!selectedDate || activeCategory !== "all"
              }
            />
          ) : (
            <div className="myOrder-orders-list">
              {filteredOrders.map((order) => (
                <OrderCard key={order.OrderNumber} order={order} />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>
      <Footer />
    </>
  );
};

export default MyOrderScreen;
